'use client'; 

import { Button } from '../buttons/Button';
import { DataTable } from '../data-display/DataTable';
import type { ColumnDef } from '@tanstack/react-table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../overlay/dialog';
import { useState } from 'react';
import type { ReactElement } from 'react';
import { cn } from '@shared/utils';
import type { DataTableSelectProps } from '../../interfaces/DataTableSelectProps';

export function DataTableSelect<T extends { id: string }>(
  props: DataTableSelectProps<T>
): ReactElement {
  const {
    data,
    isLoading,
    mappings,
    value,
    onChange,
    triggerLabel,
    dialogTitle,
    getDisplayValue
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  
  const selectedItem = data.find(item => item.id === value);
  
  const columns: ColumnDef<T>[] = [
    ...Object.entries(mappings)
      .filter(([_, mapping]) => !mapping.hideFromTable)
      .map(([key, mapping]) => ({
        accessorKey: key,
        header: mapping.label,
        cell: ({ row }: { row: { original: T } }) => {
          const item = row.original;
          const rawValue = item[key as keyof T];
          return mapping.getValue ? mapping.getValue(item) : rawValue;
        },
      })),
  ];

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="w-full justify-start">
          {value ? getDisplayValue(selectedItem) : triggerLabel}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
        </DialogHeader>
        <div className="py-4">
          <DataTable
            columns={columns}
            data={data}
            isLoading={isLoading}
            onRowClick={(item: T) => {
              onChange?.(item.id);
              setIsOpen(false);
            }}
            rowClassName={(item: T) => cn(
              'cursor-pointer hover:bg-muted/50',
              item.id === value && 'bg-muted'
            )}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}